import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { describeWriteFailure } from '../utils/writeFailure';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import SpendingSection from './SpendingSection';
import HelpButton from '../tours/HelpButton';
import useAutoTour from '../tours/useAutoTour';
import useChatIntro from '../tours/useChatIntro';
import { messageFromError } from '../utils/apiError';   // Block 233

// ── Design tokens (design file: design-refs/budget-redesign.html) ─────────────
// Reconciled with app palette: NAVY stays #1a2e3b (design uses #242629, close
// enough; matches every other component in the app).
const NAVY   = '#1a2e3b';
const BLUE   = '#82B3CC';  // design steel-blue, bills accent
const PEACH  = '#FBC490';  // design peach, spending / "out" accent
const YELLOW = '#E4E69B';  // design + app, jar lid, badges
const GREEN  = '#10b981';  // shared, income, savings
const ORANGE = '#e08a3c';  // design, child "out" label colour
const MUTED  = '#64748b';  // design muted text
const BORDER = '#e2e8f0';  // design card border
const BG     = '#f5f6f8';  // near-design #F3F3F3, reconciled with app surface

// ── Shared style objects ──────────────────────────────────────────────────────
const card = { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 18 };
const lbl  = {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600, fontSize: 11,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED,
};
const poppins = (sz, wt = 700) => ({
    fontFamily: "'Poppins', sans-serif", fontWeight: wt, fontSize: sz,
});
const inputBase = {
    border: `1px solid ${BORDER}`, borderRadius: 10,
    padding: '10px 12px',
    fontFamily: "'Raleway', sans-serif", fontSize: 16,
    outline: 'none', color: NAVY, background: '#fff', boxSizing: 'border-box',
};
const selectBase = {
    ...inputBase,
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
    paddingRight: 28, appearance: 'none', WebkitAppearance: 'none',
};
const btnPeach = {
    ...poppins(13), background: PEACH, color: NAVY,
    border: 'none', borderRadius: 12, padding: '11px 18px',
    cursor: 'pointer', whiteSpace: 'nowrap',
};
const btnGhost = {
    ...poppins(13), background: '#fff', color: NAVY,
    border: `1.5px dashed ${BORDER}`, borderRadius: 12, padding: '10px 18px',
    cursor: 'pointer',
};
const btnDanger = {
    padding: '4px 9px', borderRadius: 8,
    border: '1px solid #fca5a5', background: '#fff7f7',
    color: '#b91c1c', fontSize: 12, cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
};
const sectionTitle = { ...poppins(16, 600), color: NAVY, margin: 0 };

// Embedded responsive CSS (injected once per Budget tree)
const RESPONSIVE_CSS = `
    @keyframes bgt-floaty  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
    @keyframes bgt-floaty2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
    @media (max-width: 640px) {
        .bgt-tiles3 { grid-template-columns: 1fr 1fr !important; }
        .bgt-tiles3 > :last-child { grid-column: 1 / -1; }
        .bgt-tiles2 { grid-template-columns: 1fr !important; }
        .bgt-floaty-row { grid-template-columns: 110px 1fr 44px !important; }
        .bgt-floaty-row > .bgt-freq { display: none !important; }
        .bgt-item-chips { display: none !important; }
        .bgt-item-row { flex-wrap: wrap; gap: 8px !important; }
        .bgt-hero-lbl { font-size: 10px !important; }
        .bgt-hero-legend { font-size: 13px !important; }
        .bgt-section-sub { display: none !important; }
        .bgt-edit-row { flex-direction: column !important; }
        .bgt-edit-row > * { width: 100% !important; flex-shrink: 1 !important; }
    }
`;

// ── Helpers (Block 66/68/69 logic — unchanged) ────────────────────────────────
const fmtGBP = (n) =>
    n == null ? '—' : '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function progressPct(saved, target) {
    if (!target || Number(target) === 0) return 0;
    return Math.min(100, Math.round((Number(saved) / Number(target)) * 100));
}

function weeksToWords(weeks) {
    if (weeks == null) return null;
    if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'}`;
    const months = Math.floor(weeks / 4.33);
    return `about ${months} month${months === 1 ? '' : 's'}`;
}

function guessEmoji(name, kind) {
    const n = (name || '').toLowerCase();
    if (/salary|wage|pay|income|earning/.test(n)) return '💰';
    if (/rent|mortgage|property/.test(n)) return '🏠';
    if (/electric|gas|water|utility|utilities|energy|british gas|eon|ovo/.test(n)) return '⚡';
    if (/phone|mobile|sim|vodafone|o2|three|ee /.test(n)) return '📱';
    if (/internet|broadband|wifi|bt |virgin|sky /.test(n)) return '🌐';
    if (/car|vehicle|petrol|fuel|parking|mot|tax/.test(n)) return '🚗';
    if (/insurance/.test(n)) return '🛡️';
    if (/netflix|spotify|amazon|prime|disney|youtube|hulu|subscription/.test(n)) return '📺';
    if (/gym|sport|club|leisure/.test(n)) return '🏋️';
    if (/childcare|nursery|school|tutor/.test(n)) return '👶';
    if (/council/.test(n)) return '🏛️';
    if (/loan|credit|debt|card|finance/.test(n)) return '💳';
    if (/pension|investment|saving/.test(n)) return '📈';
    if (/food|grocery|supermarket|shop/.test(n)) return '🛒';
    return kind === 'income' ? '💰' : '💳';
}

function guessCategory(name, kind) {
    const n = (name || '').toLowerCase();
    if (kind === 'income') {
        if (/salary|wage|pay|earning/.test(n))                            return 'Wages';
        if (/benefit|universal credit|tax credit|child benefit/.test(n))  return 'Benefits';
        if (/maintenance/.test(n))                                         return 'Child maintenance';
        return 'Other';
    }
    if (/council/.test(n))                                                 return 'Taxes';
    if (/rent|mortgage|property/.test(n))                                  return 'Housing';
    if (/electric|gas|water|util|energy|british gas|e\.on|ovo|edf|npower/.test(n)) return 'Utilities';
    if (/phone|mobile|sim|vodafone|o2|three|ee |giffgaff/.test(n))        return 'Phone';
    if (/internet|broadband|wifi|bt |virgin media|sky /.test(n))          return 'Broadband & TV';
    if (/insurance/.test(n))                                               return 'Insurance';
    if (/netflix|spotify|amazon prime|disney|youtube premium|subscription/.test(n)) return 'Subscriptions';
    if (/gym|sport|club|leisure|fitness/.test(n))                          return 'Health';
    if (/childcare|nursery|school|tutor|after.?school/.test(n))            return 'Kids';
    if (/car|vehicle|petrol|fuel|parking|mot /.test(n))                   return 'Car & Transport';
    if (/loan|credit card|debt|finance|overdraft/.test(n))                 return 'Debts & Credit';
    if (/food|grocery|supermarket|shop/.test(n))                           return 'Food';
    return 'Other';
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function anchorForWeekday(dayLabel) {
    const idx   = WEEKDAY_NAMES.indexOf(dayLabel); // 0=Mon..6=Sun
    const jsDow = idx === 6 ? 0 : idx + 1;         // JS: 0=Sun..6=Sat
    const today = new Date();
    let diff = today.getDay() - jsDow;
    if (diff < 0) diff += 7;
    const anchor = new Date(today);
    anchor.setDate(today.getDate() - diff);
    return anchor.toISOString().slice(0, 10);
}

function weekDayFromAnchor(iso) {
    if (!iso) return 'Mon';
    const d = new Date(iso);
    const jsDay = d.getDay();
    return WEEKDAY_NAMES[jsDay === 0 ? 6 : jsDay - 1];
}

function MemberBadge({ member }) {
    if (!member) return null;
    return (
        <span style={{ fontSize: 11, fontWeight: 600, color: MUTED, background: '#f3f4f6', borderRadius: 99, padding: '2px 8px', marginLeft: 6 }}>
            {member.name}
        </span>
    );
}

// ── Cash flow card (Block 66 logic unchanged, styles updated) ─────────────────
function buildMonthWeeks(year, month) {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    const weeks = [];
    const dow    = first.getDay();
    const offset = dow === 0 ? -6 : 1 - dow;
    let wStart  = new Date(year, month, 1 + offset);
    while (wStart <= last) {
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        const clipStart = wStart < first ? new Date(first) : new Date(wStart);
        const clipEnd   = wEnd   > last  ? new Date(last)  : new Date(wEnd);
        const days = [];
        const cur  = new Date(clipStart);
        while (cur <= clipEnd) { days.push(cur.getDate()); cur.setDate(cur.getDate() + 1); }
        weeks.push({ start: new Date(clipStart), end: new Date(clipEnd), days });
        wStart.setDate(wStart.getDate() + 7);
    }
    return weeks;
}

function CashFlowTable({ items }) {
    // ── THE OPENING BALANCE LIVES ON THE ACCOUNT (2026-10-04) ────────────────
    //
    // It was in localStorage under 'bgt_cf_opening', so on any other device it fell
    // back to ZERO and the whole forecast silently recomputed from the wrong starting
    // point — projected balances presented as correct. Carla: "a money defect, small
    // but it tells me a number I would act on."
    //
    // In preferences, like the iCal feed URL and every other per-account setting. No
    // migration. The local value is still read as a one-time fallback so a customer
    // who set it before this change does not lose it — and the first save moves it to
    // the account.
    const { currentUser, setCurrentUser } = useCurrentUser();
    const [cfOpen,    cfToggle]   = useCollapse('bgt_open_cashflow');
    const [expanded,  setExpanded] = useState(new Set());
    const accountBal = currentUser?.preferences?.cashflowOpeningBalance;
    const [openingBal, setOpeningBal] = useState(() => {
        if (typeof accountBal === 'number') return accountBal;
        const v = localStorage.getItem('bgt_cf_opening');
        return v !== null ? Number(v) : 0;
    });
    // When the account value arrives (or changes on another tab), follow it.
    useEffect(() => {
        if (typeof accountBal === 'number') setOpeningBal(accountBal);
    }, [accountBal]);
    const [editBal,  setEditBal]  = useState(false);
    const [balInput, setBalInput] = useState('');
    const [balError, setBalError] = useState('');

    if (items.length === 0) return null;

    const now        = new Date();
    const year       = now.getFullYear();
    const month      = now.getMonth();
    const monthLabel = now.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const monthName  = now.toLocaleDateString('en-GB', { month: 'long' });
    const weeks      = buildMonthWeeks(year, month);
    const today0     = new Date(year, month, now.getDate());

    function cfFmtDay(d) { return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
    function cfWeekLabel(w) {
        if (w.start.getDate() === w.end.getDate()) return cfFmtDay(w.start);
        return `${w.start.getDate()}–${cfFmtDay(w.end)}`;
    }

    // ── bucket items into weeks ──────────────────────────────────────────────
    const weekGroups    = weeks.map(() => ({ bills: [], income: [] }));
    const anytimeBills  = [];
    const anytimeIncome = [];
    for (const item of items) {
        const day    = item.kind === 'bill' ? item.dueDay : item.payDay;
        const bucket = item.kind === 'bill' ? 'bills' : 'income';
        const any    = item.kind === 'bill' ? anytimeBills : anytimeIncome;
        if (!day) { any.push(item); continue; }
        const idx = weeks.findIndex(w => w.days.includes(day));
        if (idx === -1) { any.push(item); } else { weekGroups[idx][bucket].push(item); }
    }

    // ── build rows with running balance ─────────────────────────────────────
    let bal = openingBal;
    const rows = weeks.map((week, i) => {
        const wIn  = weekGroups[i].income.reduce((s, x) => s + Number(x.amount), 0);
        const wOut = weekGroups[i].bills.reduce((s, x)  => s + Number(x.amount), 0);
        const net  = wIn - wOut;
        bal += net;
        return {
            key:      `w${i}`,
            label:    cfWeekLabel(week),
            week,
            income:   weekGroups[i].income,
            bills:    weekGroups[i].bills,
            wIn, wOut, net, balance: bal,
            isPayday: weekGroups[i].income.length > 0,
            isThisWk: week.days.includes(now.getDate()),
            empty:    wIn === 0 && wOut === 0,
        };
    });

    const anyIn  = anytimeIncome.reduce((s, x) => s + Number(x.amount), 0);
    const anyOut = anytimeBills.reduce((s, x)  => s + Number(x.amount), 0);
    const anyNet = anyIn - anyOut;
    bal += anyNet;

    const totalIn  = rows.reduce((s, r) => s + r.wIn,  0) + anyIn;
    const totalOut = rows.reduce((s, r) => s + r.wOut, 0) + anyOut;
    const totalNet = totalIn - totalOut;

    const hasAnytime = anytimeBills.length > 0 || anytimeIncome.length > 0;

    function toggleExpand(key) {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    async function saveBal() {
        const v = parseFloat(balInput);
        if (isNaN(v)) { setEditBal(false); return; }
        setBalError('');
        try {
            const res = await api('/api/users/me', {
                method: 'PUT',
                body: JSON.stringify({ preferences: { cashflowOpeningBalance: v } }),
            });
            const data = await res.json();
            // Read the answer back: a forecast built on a number the server did not
            // keep is the defect this is fixing, one device along.
            const kept = data?.user?.preferences?.cashflowOpeningBalance;
            if (kept !== v) {
                setBalError('We could not save that to your account, so it would only apply on this device. Please try again.');
                return;
            }
            if (setCurrentUser) setCurrentUser(u => ({ ...u, preferences: data.user.preferences }));
            setOpeningBal(v);
            // The local copy is retired once the account holds it, so there is one
            // source of truth rather than two that can disagree.
            try { localStorage.removeItem('bgt_cf_opening'); } catch {}
            setEditBal(false);
        } catch (err) {
            setBalError(describeWriteFailure(err, 'save your opening balance'));
        }
    }

    // ── shared styles ────────────────────────────────────────────────────────
    const GRID    = { display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', alignItems: 'center' };
    const RA      = { textAlign: 'right' };
    const NUM     = { fontFamily: "'Poppins',sans-serif", fontWeight: 600 };
    const CF_CSS  = `
        .cf-row:hover { background: #fafcfe !important; }
        @media (max-width: 600px) {
            .cf-grid { grid-template-columns: 1.6fr 1fr 1fr 1fr !important; }
            .cf-bal  { display: none !important; }
        }
    `;

    function netColour(n) { return n > 0 ? '#0f9d6b' : n < 0 ? '#ef4444' : MUTED; }
    function fmtNet(n)    { return (n > 0 ? '+' : '') + fmtGBP(n); }

    // ── chips for an expanded detail row ────────────────────────────────────
    function chipRow(income, bills) {
        return (
            <div style={{ padding: '6px 16px 14px 30px', background: '#fcfdfe', borderBottom: `1px solid #f1f4f7`, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {income.map(x => (
                    <span key={x.id} style={{ ...NUM, fontSize: 12, padding: '3px 10px', borderRadius: 99, background: '#e7f7f0', color: '#0f9d6b' }}>
                        + {x.name} {fmtGBP(x.amount)}
                    </span>
                ))}
                {bills.map(x => (
                    <span key={x.id} style={{ ...NUM, fontSize: 12, padding: '3px 10px', borderRadius: 99, background: '#fbf1e7', color: '#b3711f' }}>
                        {x.name} {fmtGBP(x.amount)}
                    </span>
                ))}
            </div>
        );
    }

    return (
        <div style={{ ...card, marginBottom: 16 }}>
            <style>{CF_CSS}</style>

            {/* ── Collapsible header ── */}
            <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', cursor: 'pointer', userSelect: 'none' }}
                onClick={cfToggle}
            >
                <div>
                    <h3 style={{ ...poppins(20, 700), color: NAVY, margin: 0 }}>Cash flow</h3>
                    <div style={{ fontSize: 13, color: MUTED, marginTop: 3 }}>Every week at a glance</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 3 }}>
                    <span style={{ fontSize: 13, color: MUTED }}>{monthLabel}</span>
                    <Chevron open={cfOpen} />
                </div>
            </div>

            {cfOpen && (
                <>
                    {/* ── Column headers ── */}
                    <div className="cf-grid" style={{ ...GRID, padding: '10px 16px', background: '#f7f9fb', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
                        <span style={{ ...lbl }}>Week</span>
                        <span style={{ ...lbl, color: GREEN, ...RA }}>In</span>
                        <span style={{ ...lbl, color: ORANGE, ...RA }}>Out</span>
                        <span style={{ ...lbl, ...RA }}>Net</span>
                        <span className="cf-bal" style={{ ...lbl, ...RA }}>Balance</span>
                    </div>

                    {/* ── Week rows ── */}
                    {rows.map(row => {
                        const isExp  = expanded.has(row.key);
                        const rowBg  = row.isPayday ? '#f4fbf7' : row.isThisWk ? '#fffdf5' : '#fff';
                        return (
                            <React.Fragment key={row.key}>
                                <div
                                    className={`cf-grid cf-row${row.empty ? '' : ''}`}
                                    onClick={row.empty ? undefined : () => toggleExpand(row.key)}
                                    style={{ ...GRID, padding: '13px 16px', borderBottom: `1px solid #f1f4f7`, background: rowBg, cursor: row.empty ? 'default' : 'pointer', fontSize: 14, opacity: row.empty ? 0.5 : 1 }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: row.isThisWk ? NAVY : MUTED }}>
                                        <span style={{ fontSize: 10, color: '#94a3b8', display: 'inline-block', transition: 'transform 0.15s', transform: isExp ? 'rotate(90deg)' : 'none', opacity: row.empty ? 0 : 1 }}>▶</span>
                                        {row.label}
                                        {row.isPayday && row.isThisWk && <span style={{ fontSize: 11, background: YELLOW, color: NAVY, padding: '1px 8px', borderRadius: 99, fontWeight: 700 }}>payday</span>}
                                        {row.isPayday && !row.isThisWk && <span style={{ fontSize: 11, background: '#e7f7f0', color: '#0f9d6b', padding: '1px 8px', borderRadius: 99, fontWeight: 700 }}>payday</span>}
                                        {!row.isPayday && row.isThisWk && <span style={{ fontSize: 11, background: '#fef3ea', color: ORANGE, padding: '1px 8px', borderRadius: 99, fontWeight: 700 }}>this week</span>}
                                    </span>
                                    <span style={{ ...NUM, ...RA, color: row.wIn  ? GREEN  : '#cbd5e1' }}>{row.wIn  ? fmtGBP(row.wIn)  : '—'}</span>
                                    <span style={{ ...NUM, ...RA, color: row.wOut ? ORANGE : '#cbd5e1' }}>{row.wOut ? fmtGBP(row.wOut) : '—'}</span>
                                    <span style={{ ...NUM, ...RA, color: netColour(row.net) }}>{row.empty ? fmtGBP(0) : fmtNet(row.net)}</span>
                                    <span className="cf-bal" style={{ ...NUM, ...RA, color: NAVY }}>{fmtGBP(row.balance)}</span>
                                </div>
                                {isExp && !row.empty && chipRow(row.income, row.bills)}
                            </React.Fragment>
                        );
                    })}

                    {/* ── Anytime row ── */}
                    {hasAnytime && (
                        <React.Fragment>
                            <div
                                className="cf-grid cf-row"
                                onClick={() => toggleExpand('anytime')}
                                style={{ ...GRID, padding: '13px 16px', borderBottom: `1px solid #f1f4f7`, background: '#fff', cursor: 'pointer', fontSize: 14 }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: MUTED }}>
                                    <span style={{ fontSize: 10, color: '#94a3b8', display: 'inline-block', transition: 'transform 0.15s', transform: expanded.has('anytime') ? 'rotate(90deg)' : 'none' }}>▶</span>
                                    Anytime
                                </span>
                                <span style={{ ...NUM, ...RA, color: anyIn  ? GREEN  : '#cbd5e1' }}>{anyIn  ? fmtGBP(anyIn)  : '—'}</span>
                                <span style={{ ...NUM, ...RA, color: anyOut ? ORANGE : '#cbd5e1' }}>{anyOut ? fmtGBP(anyOut) : '—'}</span>
                                <span style={{ ...NUM, ...RA, color: netColour(anyNet) }}>{fmtNet(anyNet)}</span>
                                <span className="cf-bal" style={{ ...NUM, ...RA, color: NAVY }}>{fmtGBP(bal)}</span>
                            </div>
                            {expanded.has('anytime') && chipRow(anytimeIncome, anytimeBills)}
                        </React.Fragment>
                    )}

                    {/* ── Totals footer ── */}
                    <div className="cf-grid" style={{ ...GRID, padding: '14px 16px', background: '#f7f9fb', borderTop: `1px solid ${BORDER}` }}>
                        <span style={{ ...poppins(14, 700), color: NAVY }}>{monthName} total</span>
                        <span style={{ ...poppins(14, 700), color: GREEN,              ...RA }}>{fmtGBP(totalIn)}</span>
                        <span style={{ ...poppins(14, 700), color: ORANGE,             ...RA }}>{fmtGBP(totalOut)}</span>
                        <span style={{ ...poppins(14, 700), color: netColour(totalNet), ...RA }}>{fmtNet(totalNet)}</span>
                        <span className="cf-bal" style={{ ...poppins(14, 700), color: NAVY, ...RA }}>{fmtGBP(bal)}</span>
                    </div>

                    {/* ── Opening balance note ── */}
                    <div style={{ padding: '10px 16px 12px', borderTop: `1px solid ${BORDER}` }}>
                        {editBal ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 13, color: MUTED }}>Opening balance £</span>
                                <input
                                    type="number" step="0.01" value={balInput}
                                    onChange={e => setBalInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveBal(); }}
                                    style={{ ...inputBase, width: 130, fontSize: 14 }}
                                    autoFocus
                                />
                                <button style={{ ...btnPeach, padding: '7px 16px', fontSize: 13 }} onClick={saveBal}>Save</button>
                                <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 13 }} onClick={() => setEditBal(false)}>Cancel</button>
                                {/* The failure belongs AT the control, and the editor
                                    stays open so the number she typed is not lost. */}
                                {balError && (
                                    <p data-testid="opening-balance-failed" style={{ margin: 0, color: '#dc2626', fontSize: 12, flexBasis: '100%' }}>{balError}</p>
                                )}
                            </div>
                        ) : (
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                Balance assumes {fmtGBP(openingBal)} opening.{' '}
                                <button
                                    onClick={() => { setBalInput(String(openingBal || '')); setEditBal(true); }}
                                    style={{ background: 'none', border: 'none', color: BLUE, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: 'inherit' }}
                                >
                                    Set opening balance
                                </button>
                                {' '}· Tap any week to see the bills & income behind it.
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Jar SVG (child goals) ──────────────────────────────────────────────────────
function GoalJar({ goalId, pct, size = 120 }) {
    const jarH  = 106;
    const fill  = Math.min(100, Math.max(0, pct));
    const fillH = Math.round((fill / 100) * jarH);
    const fillY = 136 - fillH;
    const waveY = fillY - 4;
    const clipId   = `bgtjc_${goalId}`;
    const textY    = fillH >= 28 ? fillY + Math.round(fillH / 2) + 8 : 96;
    const textFill = fillH >= 28 ? '#fff' : '#94a3b8';
    const textSz   = fillH >= 36 ? '20' : '14';

    return (
        <svg width={size} height={Math.round(size * 1.25)} viewBox="0 0 120 150" style={{ display: 'block' }}>
            <defs>
                <clipPath id={clipId}>
                    <rect x="28" y="30" width="64" height="106" rx="16" />
                </clipPath>
            </defs>
            <rect x="28" y="30" width="64" height="106" rx="16" fill="#f4fbf6" />
            {fill > 0 && (
                <g clipPath={`url(#${clipId})`}>
                    <rect x="28" y={fillY} width="64" height={fillH + 10} fill={GREEN} />
                    <rect x="28" y={waveY} width="64" height="8" fill="#3ec98d" />
                </g>
            )}
            <rect x="28" y="30" width="64" height="106" rx="16" fill="none" stroke={NAVY} strokeWidth="2.5" />
            <rect x="34" y="14" width="52" height="18" rx="7" fill={YELLOW} stroke={NAVY} strokeWidth="2.5" />
            <text x="60" y={textY} textAnchor="middle" fontFamily="Poppins" fontWeight="800" fontSize={textSz} fill={textFill}>
                {fill}%
            </text>
        </svg>
    );
}

// ── Category presets + localStorage helpers ───────────────────────────────────
const DEFAULT_INCOME_CATS = ['Wages', 'Benefits', 'Child maintenance', 'Other'];
const DEFAULT_BILL_CATS   = [
    'Utilities', 'Taxes', 'Car & Transport', 'Phone', 'Broadband & TV',
    'Insurance', 'Housing', 'Debts & Credit', 'Subscriptions', 'Food', 'Kids', 'Health', 'Other',
];

function getCats(kind) {
    // THE CUSTOM-CATEGORY MERGE IS GONE, BECAUSE IT COULD NEVER DO ANYTHING
    // (2026-10-04). This read localStorage['bgt_income_categories'] /
    // ['bgt_bill_categories'] and merged them over the defaults — and NOTHING WRITES
    // EITHER KEY. There is no UI anywhere to create a custom budget category, so the
    // stored list was always empty and this always returned the defaults.
    //
    // Removed rather than built: a read that implies a capability the product does not
    // have is how the next person concludes custom categories exist and wonders why
    // theirs vanished. If they are ever wanted they go to the SERVER, like every other
    // per-account setting — a per-device category list is the defect we spent today
    // removing.
    return kind === 'income' ? DEFAULT_INCOME_CATS : DEFAULT_BILL_CATS;
}

// ── Floaty add row (design inline entry row) ──────────────────────────────────
function FloatyRow({ kind, members, onAdd }) {
    const BLANK = { kind, name: '', amount: '', frequency: 'monthly',
                    scheduleUI: '', weekDay: 'Mon', monthlyDay: '', intervalWeeks: '2' };
    const [form,   setForm]   = useState(BLANK);
    const [saving, setSaving] = useState(false);

    const accent = kind === 'income' ? GREEN : BLUE;
    const ready  = form.name.trim() && form.amount;

    function f(k) { return e => setForm(p => ({ ...p, [k]: e.target.value })); }

    async function save() {
        if (!ready) return;
        setSaving(true);
        const ui     = form.scheduleUI;
        const day    = form.monthlyDay ? Number(form.monthlyDay) : null;
        const st     = ui === 'monthly_day' ? 'monthly_day'
                     : (ui === 'weekly' || ui === 'custom') ? 'every_n_weeks' : null;
        const iw     = ui === 'weekly' ? 1 : ui === 'custom' ? Number(form.intervalWeeks) : null;
        const anchor = (ui === 'weekly' || ui === 'custom') ? anchorForWeekday(form.weekDay) : null;
        await onAdd('items', {
            kind,
            name:              form.name.trim(),
            amount:            Number(form.amount),
            frequency:         form.frequency,
            householdMemberId: null,
            scheduleType:      st,
            dueDay:            st === 'monthly_day' && kind === 'bill'   ? day : null,
            payDay:            st === 'monthly_day' && kind === 'income' ? day : null,
            intervalWeeks:     iw,
            anchorDate:        anchor,
            incomeCategory:    guessCategory(form.name.trim(), kind),
        });
        setForm(BLANK);
        setSaving(false);
    }

    const sui = form.scheduleUI;

    return (
        <div style={{ ...card, padding: 12, marginBottom: 10 }}>
            {/* Row 1: amount | name | frequency | + */}
            <div className="bgt-floaty-row" style={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px 44px', gap: 8, alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', ...poppins(14), color: accent, pointerEvents: 'none' }}>£</span>
                    <input type="number" min="0.01" step="0.01" placeholder="0.00" value={form.amount}
                           onChange={f('amount')} onKeyDown={e => e.key === 'Enter' && save()}
                           style={{ ...inputBase, width: '100%', paddingLeft: 26 }} />
                </div>
                <input placeholder={kind === 'income' ? 'e.g. Salary' : 'e.g. Rent'} value={form.name}
                       onChange={f('name')} onKeyDown={e => e.key === 'Enter' && save()}
                       style={{ ...inputBase, width: '100%' }} />
                <select className="bgt-freq" value={form.frequency} onChange={f('frequency')} style={{ ...selectBase, width: '100%' }}>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="yearly">Yearly</option>
                </select>
                <button onClick={save} disabled={saving || !ready}
                        style={{ ...btnPeach, padding: '9px 0', width: '100%', textAlign: 'center', fontSize: 18, opacity: ready ? 1 : 0.45 }}>
                    +
                </button>
            </div>

            {/* Row 2: schedule picker */}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={form.scheduleUI} onChange={f('scheduleUI')} style={{ ...selectBase, flex: '1 1 160px', minWidth: 0 }}>
                    <option value="">No set date</option>
                    <option value="monthly_day">Monthly on day...</option>
                    <option value="weekly">Weekly on...</option>
                    <option value="custom">Custom (every X weeks)</option>
                </select>
                {sui === 'monthly_day' && (
                    <input type="number" min="1" max="31" placeholder="Day (1-31)" value={form.monthlyDay}
                           onChange={f('monthlyDay')} style={{ ...inputBase, width: 110, flexShrink: 0 }} />
                )}
                {(sui === 'weekly' || sui === 'custom') && (
                    <>
                        {sui === 'custom' && (
                            <select value={form.intervalWeeks} onChange={f('intervalWeeks')} style={{ ...selectBase, width: 120, flexShrink: 0 }}>
                                <option value="2">Every 2 weeks</option>
                                <option value="3">Every 3 weeks</option>
                                <option value="4">Every 4 weeks</option>
                            </select>
                        )}
                        <select value={form.weekDay} onChange={f('weekDay')} style={{ ...selectBase, width: 120, flexShrink: 0 }}>
                            <option value="Mon">Monday</option>
                            <option value="Tue">Tuesday</option>
                            <option value="Wed">Wednesday</option>
                            <option value="Thu">Thursday</option>
                            <option value="Fri">Friday</option>
                            <option value="Sat">Saturday</option>
                            <option value="Sun">Sunday</option>
                        </select>
                    </>
                )}
            </div>
        </div>
    );
}

// ── Item list row ─────────────────────────────────────────────────────────────
function ItemRow({ item, onDelete, onEdit, last }) {
    const [confirm, setConfirm] = useState(false);
    const [editing, setEditing] = useState(false);
    const [ef,      setEf]      = useState(null);
    const isIncome = item.kind === 'income';
    const dot      = isIncome ? GREEN : BLUE;
    const amtColor = isIncome ? GREEN : NAVY;
    const freq     = item.frequency === 'monthly' ? 'mo' : item.frequency === 'weekly' ? 'wk' : 'yr';

    function startEdit() {
        const sui = !item.scheduleType ? ''
                  : item.scheduleType === 'monthly_day' ? 'monthly_day'
                  : item.intervalWeeks === 1 ? 'weekly' : 'custom';
        setEf({
            amount:        String(item.amount),
            name:          item.name,
            frequency:     item.frequency || 'monthly',
            scheduleUI:    sui,
            monthlyDay:    String(item.dueDay || item.payDay || ''),
            intervalWeeks: String(item.intervalWeeks || '2'),
            weekDay:       weekDayFromAnchor(item.anchorDate),
        });
        setEditing(true);
    }

    function ef_f(k) { return e => setEf(p => ({ ...p, [k]: e.target.value })); }

    async function saveEdit() {
        if (!ef || !ef.name.trim() || !ef.amount) return;
        const ui     = ef.scheduleUI;
        const day    = ef.monthlyDay ? Number(ef.monthlyDay) : null;
        const st     = ui === 'monthly_day' ? 'monthly_day'
                     : (ui === 'weekly' || ui === 'custom') ? 'every_n_weeks' : null;
        const iw     = ui === 'weekly' ? 1 : ui === 'custom' ? Number(ef.intervalWeeks) : null;
        const anchor = (ui === 'weekly' || ui === 'custom') ? anchorForWeekday(ef.weekDay) : null;
        await onEdit(item.id, {
            name:          ef.name.trim(),
            amount:        Number(ef.amount),
            frequency:     ef.frequency,
            scheduleType:  st,
            dueDay:        st === 'monthly_day' && item.kind === 'bill'   ? day : null,
            payDay:        st === 'monthly_day' && item.kind === 'income' ? day : null,
            intervalWeeks: iw,
            anchorDate:    anchor,
            incomeCategory: guessCategory(ef.name.trim(), item.kind),
        });
        setEditing(false);
    }

    return (
        <div style={{ padding: '13px 16px', borderBottom: last ? 'none' : `1px solid #f1f4f7` }}>
            {!editing ? (
                <>
                    <div className="bgt-item-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: dot, flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ flex: 1, fontWeight: 600, fontSize: 16, color: NAVY, minWidth: 0 }}>
                            {item.name}
                            <MemberBadge member={item.householdMember} />
                        </span>
                        <span className="bgt-item-chips" style={{ fontSize: 12, color: MUTED, background: '#f3f5f7', padding: '3px 9px', borderRadius: 99, whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {guessEmoji(item.name, item.kind)} {isIncome ? 'Income' : 'Bill'}
                        </span>
                        {item.incomeCategory && (
                            <span className="bgt-item-chips" style={{ fontSize: 12, color: isIncome ? GREEN : BLUE, background: isIncome ? '#ecfdf5' : '#eff6ff', padding: '3px 9px', borderRadius: 99, whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 600 }}>
                                {item.incomeCategory}
                            </span>
                        )}
                        <span style={{ ...poppins(16), color: amtColor, minWidth: 72, textAlign: 'right', flexShrink: 0 }}>
                            {isIncome ? '+' : ''}{fmtGBP(item.amount)}
                            <span style={{ fontWeight: 400, fontSize: 13, color: MUTED }}>/{freq}</span>
                        </span>
                        <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 14 }} onClick={startEdit}>edit</button>
                        <button style={btnDanger} onClick={() => setConfirm(true)}>×</button>
                    </div>
                    {confirm && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, paddingLeft: 20 }}>
                            <span style={{ fontSize: 14, color: MUTED }}>Remove {item.name}?</span>
                            <button style={{ ...btnDanger, padding: '3px 10px' }} onClick={() => onDelete('items', item.id)}>Yes</button>
                            <button style={{ ...btnGhost, padding: '3px 10px', fontSize: 12 }} onClick={() => setConfirm(false)}>No</button>
                        </div>
                    )}
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ position: 'relative', width: 110, flexShrink: 0 }}>
                            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', ...poppins(13), color: isIncome ? GREEN : BLUE, pointerEvents: 'none' }}>£</span>
                            <input type="number" min="0.01" step="0.01" value={ef.amount} onChange={ef_f('amount')}
                                   style={{ ...inputBase, width: '100%', paddingLeft: 26 }} autoFocus />
                        </div>
                        <input value={ef.name} onChange={ef_f('name')} style={{ ...inputBase, flex: '1 1 120px', minWidth: 0 }} />
                        <select value={ef.frequency} onChange={ef_f('frequency')} style={{ ...selectBase, width: 120, flexShrink: 0 }}>
                            <option value="monthly">Monthly</option>
                            <option value="weekly">Weekly</option>
                            <option value="yearly">Yearly</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select value={ef.scheduleUI} onChange={ef_f('scheduleUI')} style={{ ...selectBase, flex: '1 1 140px', minWidth: 0 }}>
                            <option value="">No set date</option>
                            <option value="monthly_day">Monthly on day...</option>
                            <option value="weekly">Weekly on...</option>
                            <option value="custom">Custom (every X weeks)</option>
                        </select>
                        {ef.scheduleUI === 'monthly_day' && (
                            <input type="number" min="1" max="31" placeholder="Day (1-31)" value={ef.monthlyDay}
                                   onChange={ef_f('monthlyDay')} style={{ ...inputBase, width: 100, flexShrink: 0 }} />
                        )}
                        {(ef.scheduleUI === 'weekly' || ef.scheduleUI === 'custom') && (
                            <>
                                {ef.scheduleUI === 'custom' && (
                                    <select value={ef.intervalWeeks} onChange={ef_f('intervalWeeks')} style={{ ...selectBase, width: 120, flexShrink: 0 }}>
                                        <option value="2">Every 2 weeks</option>
                                        <option value="3">Every 3 weeks</option>
                                        <option value="4">Every 4 weeks</option>
                                    </select>
                                )}
                                <select value={ef.weekDay} onChange={ef_f('weekDay')} style={{ ...selectBase, width: 120, flexShrink: 0 }}>
                                    <option value="Mon">Monday</option>
                                    <option value="Tue">Tuesday</option>
                                    <option value="Wed">Wednesday</option>
                                    <option value="Thu">Thursday</option>
                                    <option value="Fri">Friday</option>
                                    <option value="Sat">Saturday</option>
                                    <option value="Sun">Sunday</option>
                                </select>
                            </>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button style={{ ...btnPeach, padding: '7px 16px', fontSize: 13 }} onClick={saveEdit}>Save</button>
                        <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 13 }} onClick={() => setEditing(false)}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Hero card ("Left to spend this month") ────────────────────────────────────
function HeroCard({ headroom, spendingTotal, safespend: ss }) {
    if (!headroom || headroom.incomeMonthly === 0) {
        return (
            <div style={{ ...card, padding: '22px 24px', marginBottom: 16 }}>
                <div style={{ ...lbl, marginBottom: 8 }}>Left to spend this month</div>
                <div style={{ fontSize: 13, color: MUTED }}>
                    Add your income below to see your monthly picture.
                </div>
            </div>
        );
    }

    const income   = headroom.incomeMonthly;
    const bills    = headroom.billsMonthly;
    const spending = spendingTotal || 0;
    const left     = income - bills - spending;
    const leftColour = left >= 0 ? NAVY : '#ef4444';

    const billsPct    = income > 0 ? Math.min(100,                       Math.round((bills    / income) * 100)) : 0;
    const spendingPct = income > 0 ? Math.min(100 - billsPct,            Math.round((spending / income) * 100)) : 0;
    const leftPct     = Math.max(0, 100 - billsPct - spendingPct);

    // Safe-spend: show amber notice when spending has eaten into the bills buffer
    const showWarn = ss && ss.keepAside > 0 && (income - ss.keepAside - spending) < 0;

    return (
        <div style={{ ...card, padding: '22px 24px', marginBottom: 16 }}>
            <div className="bgt-hero-lbl" style={{ ...lbl, marginBottom: 8 }}>Left to spend this month</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, margin: '4px 0 14px', flexWrap: 'wrap' }}>
                <div style={{ ...poppins(44), lineHeight: 1, color: leftColour }}>{fmtGBP(left)}</div>
                <div style={{ fontSize: 16, color: MUTED }}>of {fmtGBP(income)} income</div>
            </div>
            {/* 3-segment bar: bills (blue) / spending (orange) / leftover (green) */}
            <div style={{ height: 14, background: '#eef1f4', borderRadius: 99, overflow: 'hidden', display: 'flex' }}>
                {billsPct    > 0 && <div style={{ width: `${billsPct}%`,    background: BLUE,   transition: 'width 0.4s' }} />}
                {spendingPct > 0 && <div style={{ width: `${spendingPct}%`, background: ORANGE, transition: 'width 0.4s' }} />}
                {leftPct     > 0 && <div style={{ width: `${leftPct}%`,     background: GREEN,  transition: 'width 0.4s' }} />}
            </div>
            <div className="bgt-hero-legend" style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 14, color: MUTED, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i style={{ width: 10, height: 10, borderRadius: 3, background: BLUE, display: 'inline-block', flexShrink: 0 }} />
                    Bills {fmtGBP(bills)}
                </span>
                {spending > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ width: 10, height: 10, borderRadius: 3, background: ORANGE, display: 'inline-block', flexShrink: 0 }} />
                        Spending {fmtGBP(spending)}
                    </span>
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i style={{ width: 10, height: 10, borderRadius: 3, background: left >= 0 ? GREEN : '#ef4444', display: 'inline-block', flexShrink: 0 }} />
                    {left >= 0 ? 'Left' : 'Over'} {fmtGBP(Math.abs(left))}
                </span>
            </div>
            {ss && ss.keepAside > 0 && (
                <div style={{ marginTop: 12, padding: '12px 14px', background: showWarn ? '#fffbeb' : '#f0fdf4', borderRadius: 10, borderLeft: `3px solid ${showWarn ? '#f59e0b' : GREEN}`, fontSize: 14, color: showWarn ? '#92400e' : '#065f46' }}>
                    {(() => {
                        const byDate = ss.finalBillDate || ss.nextBillDate;
                        const dateStr = byDate ? ` by ${fmtDate(byDate)}` : '';
                        return showWarn
                            ? `Heads up: keep at least ${fmtGBP(ss.keepAside)} aside for bills due${dateStr}. After today's spending your flexible money is a bit tight.`
                            : `Keep ${fmtGBP(ss.keepAside)} aside for upcoming bills${dateStr}.`;
                    })()}
                </div>
            )}
        </div>
    );
}

// ── Quick tiles (3-col) ───────────────────────────────────────────────────────
function QuickTiles({ headroom, spendingTotal, items }) {
    const income   = headroom?.incomeMonthly || 0;
    const bills    = headroom?.billsMonthly  || 0;
    const spending = spendingTotal           || 0;

    const now  = new Date();
    const dow  = now.getDay();
    const wMon = new Date(now); wMon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    // Build the set of {day, month} pairs in this Mon–Sun so month-boundary weeks work correctly
    const weekSlots = [];
    const cur = new Date(wMon);
    for (let i = 0; i < 7; i++) {
        weekSlots.push({ day: cur.getDate(), month: cur.getMonth() });
        cur.setDate(cur.getDate() + 1);
    }
    const dueThisWeekItems = (items || []).filter(i =>
        i.kind === 'bill' && i.dueDay &&
        weekSlots.some(s => s.day === i.dueDay && s.month === now.getMonth())
    );
    const dueThisWeekCount = dueThisWeekItems.length;
    const dueThisWeekAmt   = dueThisWeekItems.reduce((s, i) => s + Number(i.amount), 0);
    const dueThisWeekSub   = dueThisWeekCount > 0
        ? `${dueThisWeekCount} due this week · ${fmtGBP(dueThisWeekAmt)}`
        : null;

    const tiles = [
        { key: 'in',  label: 'Money in',  dot: GREEN, value: income,   sub: null },
        { key: 'out', label: 'Bills due',  dot: BLUE,  value: bills,    sub: dueThisWeekSub },
        { key: 'sp',  label: 'Spending',   dot: PEACH, value: spending, sub: null },
    ];

    return (
        <div className="bgt-tiles3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            {tiles.map(t => (
                <div key={t.key} style={{ ...card, padding: '16px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 3, background: t.dot, display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ ...lbl, letterSpacing: '0.04em' }}>{t.label}</span>
                    </div>
                    <div style={{ ...poppins(22), marginTop: 8, color: NAVY }}>{fmtGBP(t.value)}</div>
                    {t.sub && <div style={{ fontSize: 13, color: '#f59e0b', marginTop: 2, fontWeight: 600 }}>{t.sub}</div>}
                </div>
            ))}
        </div>
    );
}

// ── Goal card (redesigned — Block 68 maths logic unchanged) ───────────────────
function GoalCard({ goal, onDelete, onContribute }) {
    const [showContrib, setShowContrib] = useState(false);
    const [amount, setAmount]           = useState('');
    const [confirmDel,  setConfirmDel]  = useState(false);
    const pct      = progressPct(goal.savedSoFar, goal.targetAmount);
    const member   = goal.householdMember;
    const isChild  = member?.role === 'child';
    const isPocket = isChild && goal.weeklyIncome;
    const headline = member ? `${member.name}'s ${goal.name}` : goal.name;
    const barColor = pct >= 100 ? GREEN : isChild ? '#f59e0b' : GREEN;

    function mathLine() {
        const m = goal.maths;
        if (!m) return null;
        if (m.complete) return <span style={{ color: GREEN, fontWeight: 700 }}>Goal reached.</span>;
        if (Array.isArray(m)) {
            if (isPocket && goal.pocketMoney) {
                const pm = goal.pocketMoney;
                const p3 = pm.projections.find(p => p.months === 3);
                const p6 = pm.projections.find(p => p.months === 6);
                const p9 = pm.projections.find(p => p.months === 9);
                const p12 = pm.projections.find(p => p.months === 12);
                return (
                    <span>
                        Save half (£{pm.halfWeekly}/wk): £{p3?.savedTotal} in 3 months, £{p6?.savedTotal} in 6, £{p9?.savedTotal} in 9, £{p12?.savedTotal} in 12
                        {pm.weeksToGoal ? `, hits the target in ${weeksToWords(pm.weeksToGoal)}.` : '.'}
                    </span>
                );
            }
            const m6  = m.find(p => p.months === 6);
            const m12 = m.find(p => p.months === 12);
            return <span>At current pace: {fmtGBP(m6?.savedTotal)} in 6 months, {fmtGBP(m12?.savedTotal)} in 12.</span>;
        }
        if (!m.weeklyNeeded) return <span style={{ color: '#ef4444' }}>Target date has passed.</span>;
        return (
            <span style={{ color: m.unrealistic ? '#f59e0b' : MUTED }}>
                Save {fmtGBP(m.weeklyNeeded)}/week to reach it by {fmtDate(goal.targetDate)} ({m.weeks} weeks away).
                {m.unrealistic ? ' This looks tight given your headroom.' : ''}
            </span>
        );
    }

    function trackBadge() {
        const m = goal.maths;
        if (!m || Array.isArray(m)) return null;
        if (m.complete) return <span style={{ color: GREEN, fontWeight: 600 }}>Complete</span>;
        if (!m.weeklyNeeded) return null;
        if (goal.targetDate)
            return <span style={{ color: m.unrealistic ? '#f59e0b' : GREEN, fontWeight: 600 }}>{m.unrealistic ? 'Tight' : 'On track'}</span>;
        return null;
    }

    async function submitContrib() {
        if (!amount || Number(amount) <= 0) return;
        await onContribute(goal.id, Number(amount));
        setAmount('');
        setShowContrib(false);
    }

    return (
        <div style={{ ...card, padding: '16px 18px', ...(isChild ? { borderLeft: `4px solid #f59e0b`, background: '#fffbeb' } : {}) }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 9 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: NAVY, flex: 1 }}>
                    {isChild && <span style={{ marginRight: 4 }}>{pct >= 100 ? '🎉' : isPocket ? '🪙' : '⭐'}</span>}
                    {headline}
                    {!isChild && <MemberBadge member={member} />}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 13, color: MUTED }}>
                        <b style={{ color: NAVY }}>{fmtGBP(goal.savedSoFar)}</b> / {fmtGBP(goal.targetAmount)}
                    </span>
                    {confirmDel ? (
                        <>
                            <span style={{ fontSize: 12, color: MUTED }}>Delete?</span>
                            <button style={{ ...btnDanger, padding: '3px 8px' }} onClick={() => onDelete('goals', goal.id)}>Yes</button>
                            <button style={{ ...btnGhost, padding: '3px 8px', fontSize: 12 }} onClick={() => setConfirmDel(false)}>No</button>
                        </>
                    ) : (
                        <button style={btnDanger} onClick={() => setConfirmDel(true)}>×</button>
                    )}
                </div>
            </div>
            <div style={{ height: 10, background: '#eef1f4', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 99, transition: 'width 0.4s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11, color: MUTED }}>
                <span>{isChild && pct >= 100 ? 'Goal reached! 🎊' : isChild ? `${pct}% there, keep going!` : `${pct}% saved`}</span>
                <span>{trackBadge() || (goal.targetDate ? null : <span>{fmtGBP(Number(goal.targetAmount) - Number(goal.savedSoFar))} to go</span>)}</span>
            </div>
            {!isChild && <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{mathLine()}</div>}
            {isChild && pct < 100 && <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{mathLine()}</div>}

            {showContrib ? (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                        type="number" min="0.01" step="0.01" placeholder="£ amount"
                        value={amount} onChange={e => setAmount(e.target.value)}
                        style={{ ...inputBase, width: 120, flexShrink: 0 }}
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && submitContrib()}
                    />
                    <button style={btnPeach} onClick={submitContrib}>Log it</button>
                    <button style={{ ...btnGhost, padding: '9px 14px' }} onClick={() => setShowContrib(false)}>Cancel</button>
                </div>
            ) : (
                <button style={{ ...btnGhost, marginTop: 10, fontSize: 12 }} onClick={() => setShowContrib(true)}>
                    + Add contribution
                </button>
            )}
        </div>
    );
}

function GoalsSection({ goals, members, onAdd, onDelete, onContribute, childMode = false }) {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', targetAmount: '', targetDate: '', weeklyIncome: '', householdMemberId: '' });

    function f(k) { return e => setForm(p => ({ ...p, [k]: e.target.value })); }

    async function save() {
        if (!form.name.trim() || !form.targetAmount) return;
        await onAdd('goals', {
            name:              form.name.trim(),
            targetAmount:      Number(form.targetAmount),
            targetDate:        form.targetDate || null,
            weeklyIncome:      form.weeklyIncome ? Number(form.weeklyIncome) : null,
            householdMemberId: form.householdMemberId || null,
        });
        setForm({ name: '', targetAmount: '', targetDate: '', weeklyIncome: '', householdMemberId: '' });
        setShowForm(false);
    }

    const selMember = members.find(m => m.id === form.householdMemberId);
    const selChild  = selMember?.role === 'child';

    return (
        <div style={{ marginBottom: 22 }}>
            {!childMode && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={sectionTitle}>Savings goals</h3>
                    {goals.length > 0 && (
                        <span style={{ ...poppins(12, 600), background: YELLOW, color: NAVY, borderRadius: 99, padding: '2px 10px' }}>
                            {goals.length} active
                        </span>
                    )}
                </div>
            )}
            {goals.length === 0 && !showForm && !childMode && (
                <p style={{ fontSize: 13, color: MUTED, fontStyle: 'italic', marginBottom: 8 }}>
                    No goals yet, add a family holiday fund, a bike goal for the kids, or anything you are saving toward.
                </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: goals.length > 0 ? 10 : 0 }}>
                {goals.map(g => <GoalCard key={g.id} goal={g} onDelete={onDelete} onContribute={onContribute} />)}
            </div>
            {showForm ? (
                <div style={{ ...card, padding: 16, marginBottom: 10, background: '#f9fafb' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ flex: '2 1 160px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Goal name</div>
                            <input style={{ ...inputBase, width: '100%' }} value={form.name} onChange={f('name')} placeholder={childMode ? 'e.g. New bike, Nintendo Switch' : 'e.g. Family holiday, Bike fund'} autoFocus required />
                        </div>
                        <div style={{ flex: '1 1 100px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Target (£)</div>
                            <input style={{ ...inputBase, width: '100%' }} type="number" min="0.01" step="0.01" value={form.targetAmount} onChange={f('targetAmount')} required />
                        </div>
                        {!childMode && (
                            <div style={{ flex: '1 1 140px' }}>
                                <div style={{ ...lbl, marginBottom: 4 }}>Target date (optional)</div>
                                <input style={{ ...inputBase, width: '100%' }} type="date" value={form.targetDate} onChange={f('targetDate')} />
                            </div>
                        )}
                    </div>
                    {!childMode && members.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>For (optional)</div>
                            <select style={{ ...selectBase, width: '100%' }} value={form.householdMemberId} onChange={f('householdMemberId')}>
                                <option value="">Whole family</option>
                                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>
                    )}
                    {(childMode || selChild) && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Weekly pocket money (optional)</div>
                            <input style={{ ...inputBase, width: '100%' }} type="number" min="0.01" step="0.01" value={form.weeklyIncome} onChange={f('weeklyIncome')} placeholder="e.g. 10" />
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button style={btnPeach} onClick={save}>{childMode ? 'Start this jar' : 'Save goal'}</button>
                        <button style={{ ...btnGhost, padding: '9px 14px' }} onClick={() => setShowForm(false)}>Cancel</button>
                    </div>
                </div>
            ) : (
                <button style={{ ...btnGhost, ...(childMode ? { borderColor: GREEN, color: GREEN, width: '100%' } : {}) }} onClick={() => setShowForm(true)}>
                    {childMode ? '+ New jar' : '+ Add goal'}
                </button>
            )}
        </div>
    );
}

// ── Debt card (Block 69 logic unchanged) ──────────────────────────────────────
function DebtCard({ debt, onDelete, onPatch }) {
    const m         = debt.maths;
    const effective = debt.effectiveBalance ?? debt.balance;
    const paidTotal = debt.paidTotal || 0;
    const total     = paidTotal + effective;
    const paidPct   = total > 0 ? Math.round((paidTotal / total) * 100) : 0;
    const [correcting,  setCorrecting]  = useState(false);
    const [newBal,      setNewBal]      = useState('');
    const [confirmDel,  setConfirmDel]  = useState(false);

    async function submitCorrection(e) {
        e.preventDefault();
        if (!newBal || isNaN(Number(newBal))) return;
        await onPatch(debt.id, { balance: Number(newBal) });
        setCorrecting(false);
        setNewBal('');
    }

    const doneDateLabel = m?.debtFreeDate
        ? new Date(m.debtFreeDate).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
        : null;

    return (
        <div style={{ ...card, padding: '16px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 9 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>🏦 {debt.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 13, color: MUTED }}>
                        <b style={{ color: '#ef4444' }}>{fmtGBP(effective)}</b> left
                    </span>
                    {confirmDel ? (
                        <>
                            <span style={{ fontSize: 12, color: MUTED }}>Delete?</span>
                            <button style={{ ...btnDanger, padding: '3px 8px' }} onClick={() => onDelete('debts', debt.id)}>Yes</button>
                            <button style={{ ...btnGhost, padding: '3px 8px', fontSize: 12 }} onClick={() => setConfirmDel(false)}>No</button>
                        </>
                    ) : (
                        <button style={btnDanger} onClick={() => setConfirmDel(true)}>×</button>
                    )}
                </div>
            </div>
            {total > 0 && (
                <div style={{ height: 10, background: '#fdecec', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${paidPct}%`, background: GREEN, height: '100%', borderRadius: 99, transition: 'width 0.4s' }} />
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11, color: MUTED }}>
                <span>{paidPct}% paid off</span>
                <span style={{ fontWeight: 600 }}>
                    {fmtGBP(debt.monthlyPayment)}/mo{doneDateLabel ? ` · done ${doneDateLabel}` : ''}
                </span>
            </div>
            {m?.neverClears && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                    Monthly payment may not cover interest, consider increasing it.
                </div>
            )}
            {debt.monthlyInterest > 0 && (
                <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                    About {fmtGBP(debt.monthlyInterest)}/month in interest right now.
                </div>
            )}
            {debt.targetClearDate && (
                <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>Target clear date: {fmtDate(debt.targetClearDate)}</div>
            )}
            {correcting ? (
                <form onSubmit={submitCorrection} style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                    <input style={{ ...inputBase, width: 110 }} type="number" min="0" step="0.01" placeholder="New balance" value={newBal} onChange={e => setNewBal(e.target.value)} autoFocus />
                    <button type="submit" style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }}>Save</button>
                    <button type="button" style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }} onClick={() => setCorrecting(false)}>Cancel</button>
                </form>
            ) : (
                <button style={{ fontSize: 11, color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0 0', marginTop: 2 }}
                    onClick={() => { setNewBal(String(effective)); setCorrecting(true); }}>
                    Statement different? Adjust balance
                </button>
            )}
        </div>
    );
}

function DebtsSection({ debts, onAdd, onDelete, onPatch }) {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', balance: '', monthlyPayment: '', apr: '', targetClearDate: '' });

    function f(k) { return e => setForm(p => ({ ...p, [k]: e.target.value })); }

    async function save() {
        if (!form.name.trim() || !form.balance || !form.monthlyPayment) return;
        await onAdd('debts', {
            name:            form.name.trim(),
            balance:         Number(form.balance),
            monthlyPayment:  Number(form.monthlyPayment),
            apr:             form.apr ? Number(form.apr) : null,
            targetClearDate: form.targetClearDate || null,
        });
        setForm({ name: '', balance: '', monthlyPayment: '', apr: '', targetClearDate: '' });
        setShowForm(false);
    }

    return (
        <div style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={sectionTitle}>Debts</h3>
                <span style={{ fontSize: 12, color: MUTED }}>Chipping away, no pressure</span>
            </div>
            {debts.length === 0 && !showForm && (
                <p style={{ fontSize: 13, color: MUTED, margin: '0 0 8px' }}>
                    Track a debt here if you would like help chipping away at it. No pressure, it is here when you are ready.
                </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: debts.length > 0 ? 10 : 0 }}>
                {debts.map(d => <DebtCard key={d.id} debt={d} onDelete={onDelete} onPatch={onPatch} />)}
            </div>
            {showForm ? (
                <div style={{ ...card, padding: 16, marginBottom: 10, background: '#f9fafb' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ flex: '2 1 150px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Name</div>
                            <input style={{ ...inputBase, width: '100%' }} value={form.name} onChange={f('name')} placeholder="e.g. Car loan, Credit card" autoFocus required />
                        </div>
                        <div style={{ flex: '1 1 90px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Balance (£)</div>
                            <input style={{ ...inputBase, width: '100%' }} type="number" min="0.01" step="0.01" value={form.balance} onChange={f('balance')} required />
                        </div>
                        <div style={{ flex: '1 1 90px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Min payment (£)</div>
                            <input style={{ ...inputBase, width: '100%' }} type="number" min="0.01" step="0.01" value={form.monthlyPayment} onChange={f('monthlyPayment')} required />
                        </div>
                        <div style={{ flex: '1 1 70px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>APR % (opt)</div>
                            <input style={{ ...inputBase, width: '100%' }} type="number" min="0" step="0.01" value={form.apr} onChange={f('apr')} placeholder="e.g. 19.9" />
                        </div>
                        <div style={{ flex: '1 1 120px' }}>
                            <div style={{ ...lbl, marginBottom: 4 }}>Target date (opt)</div>
                            <input style={{ ...inputBase, width: '100%' }} type="date" value={form.targetClearDate} onChange={f('targetClearDate')} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button style={btnPeach} onClick={save}>Save</button>
                        <button style={{ ...btnGhost, padding: '9px 14px' }} onClick={() => setShowForm(false)}>Cancel</button>
                    </div>
                </div>
            ) : (
                <button style={btnGhost} onClick={() => setShowForm(true)}>+ Add debt</button>
            )}
        </div>
    );
}

// ── Collapse chevron helper ───────────────────────────────────────────────────
function Chevron({ open }) {
    return (
        <span style={{ display: 'inline-block', fontSize: 11, color: MUTED, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', marginLeft: 6, userSelect: 'none' }}>
            ▶
        </span>
    );
}

function useCollapse(lsKey, defaultOpen = true) {
    const [open, setOpen] = useState(() => {
        const stored = localStorage.getItem(lsKey);
        return stored !== null ? stored !== '0' : defaultOpen;
    });
    function toggle() {
        const next = !open;
        setOpen(next);
        localStorage.setItem(lsKey, next ? '1' : '0');
    }
    return [open, toggle];
}

// ── Incomings / Outgoings sections ────────────────────────────────────────────
function IncomingsSection({ items, members, headroom, onAdd, onDelete, onEdit }) {
    const incomes = items.filter(i => i.kind === 'income');
    const [open, toggle] = useCollapse('bgt_open_incomings', incomes.length === 0);
    const total   = headroom?.incomeMonthly || 0;
    return (
        <div style={{ marginBottom: 22 }} id="bgt-incomings">
            <div style={{ ...(!open ? { ...card, padding: '14px 16px' } : {}) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 12 : 0, cursor: 'pointer', userSelect: 'none' }} onClick={toggle}>
                    <h3 style={{ ...sectionTitle, display: 'flex', alignItems: 'center' }}>
                        Incomings <Chevron open={open} />
                    </h3>
                    <span className="bgt-section-sub" style={{ fontSize: 14, color: MUTED }}>This month · <b style={{ color: GREEN }}>{fmtGBP(total)}</b></span>
                </div>
            </div>
            {open && (
                <>
                    <FloatyRow kind="income" members={members} onAdd={onAdd} />
                    {incomes.length > 0 ? (
                        <div style={{ ...card, overflow: 'hidden' }}>
                            {incomes.map((item, i) => (
                                <ItemRow key={item.id} item={item} onDelete={onDelete} onEdit={onEdit} last={i === incomes.length - 1} />
                            ))}
                        </div>
                    ) : (
                        <div style={{ ...card, padding: '18px 16px', textAlign: 'center', color: MUTED, fontSize: 13, borderStyle: 'dashed' }}>
                            No income added yet, add your salary or wages above.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function OutgoingsSection({ items, members, headroom, onAdd, onDelete, onEdit }) {
    const bills = items.filter(i => i.kind === 'bill');
    const [open, toggle] = useCollapse('bgt_open_outgoings', bills.length === 0);
    const total = headroom?.billsMonthly || 0;
    return (
        <div style={{ marginBottom: 22 }}>
            <div style={{ ...(!open ? { ...card, padding: '14px 16px' } : {}) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 12 : 0, cursor: 'pointer', userSelect: 'none' }} onClick={toggle}>
                    <h3 style={{ ...sectionTitle, display: 'flex', alignItems: 'center' }}>
                        Outgoings <Chevron open={open} />
                    </h3>
                    <span className="bgt-section-sub" style={{ fontSize: 14, color: MUTED }}>This month · <b style={{ color: NAVY }}>{fmtGBP(total)}</b></span>
                </div>
            </div>
            {open && (
                <>
                    <FloatyRow kind="bill" members={members} onAdd={onAdd} />
                    {bills.length > 0 ? (
                        <div style={{ ...card, overflow: 'hidden' }}>
                            {bills.map((item, i) => (
                                <ItemRow key={item.id} item={item} onDelete={onDelete} onEdit={onEdit} last={i === bills.length - 1} />
                            ))}
                        </div>
                    ) : (
                        <div style={{ ...card, padding: '18px 16px', textAlign: 'center', color: MUTED, fontSize: 13, borderStyle: 'dashed' }}>
                            No bills added yet, add rent, utilities, and subscriptions above.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ── Child: jar hero (single goal) ─────────────────────────────────────────────
function JarHero({ goal, onContribute }) {
    const [amount, setAmount] = useState('');
    const [saving, setSaving] = useState(false);
    const pct  = progressPct(goal.savedSoFar, goal.targetAmount);
    const left = Number(goal.targetAmount) - Number(goal.savedSoFar);

    async function addContrib() {
        if (!amount || Number(amount) <= 0) return;
        setSaving(true);
        await onContribute(goal.id, Number(amount));
        setAmount('');
        setSaving(false);
    }

    return (
        <div style={{ ...card, padding: '24px 26px', marginBottom: 20, display: 'flex', gap: 26, alignItems: 'center', background: 'linear-gradient(160deg, #fff, #eef8f1)', flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0, animation: 'bgt-floaty2 5s ease-in-out infinite' }}>
                <GoalJar goalId={goal.id} pct={pct} size={120} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ ...lbl, color: GREEN, marginBottom: 4 }}>Saving for</div>
                <h3 style={{ ...poppins(22), color: NAVY, margin: '0 0 4px' }}>{goal.name}</h3>
                <div style={{ fontSize: 14, color: MUTED, marginBottom: 12 }}>
                    <b style={{ ...poppins(20), color: NAVY }}>{fmtGBP(goal.savedSoFar)}</b>
                    {' '}of {fmtGBP(goal.targetAmount)} · <b style={{ color: GREEN }}>{fmtGBP(left)} to go</b>
                </div>
                <div style={{ height: 16, background: '#e6efe9', borderRadius: 99, overflow: 'hidden', marginBottom: 14 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: GREEN, borderRadius: 99, transition: 'width 0.4s' }} />
                </div>
                {pct >= 100 ? (
                    <div style={{ ...poppins(16), color: GREEN }}>Goal reached! 🎊</div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ background: '#fff', border: `2px solid ${GREEN}`, borderRadius: 14, padding: '7px 8px 7px 14px', display: 'flex', alignItems: 'center', gap: 8, boxShadow: `0 6px 16px rgba(16,185,129,.14)` }}>
                            <span style={{ ...poppins(16), color: GREEN, flexShrink: 0 }}>£</span>
                            <input
                                type="number" min="0.01" step="0.01" placeholder="Add to jar"
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addContrib()}
                                style={{ flex: 1, border: 'none', outline: 'none', fontFamily: "'Poppins',sans-serif", fontWeight: 600, fontSize: 15, background: 'transparent', minWidth: 0, color: NAVY, width: 110 }}
                            />
                            <button
                                onClick={addContrib}
                                disabled={saving}
                                style={{ background: GREEN, color: '#fff', border: 'none', width: 40, height: 40, borderRadius: 12, fontSize: 20, cursor: 'pointer', flexShrink: 0 }}
                            >+</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Child: jar shelf (multiple goals) ─────────────────────────────────────────
function JarShelf({ goals, onContribute }) {
    const [activeId, setActiveId] = useState(null);
    const [amount,   setAmount]   = useState('');
    const [saving,   setSaving]   = useState(false);
    const totalSaved = goals.reduce((s, g) => s + Number(g.savedSoFar), 0);

    async function addContrib(goalId) {
        if (!amount || Number(amount) <= 0) return;
        setSaving(true);
        await onContribute(goalId, Number(amount));
        setAmount('');
        setActiveId(null);
        setSaving(false);
    }

    return (
        <div style={{ ...card, padding: '24px 22px 16px', marginBottom: 20, background: 'linear-gradient(180deg, #fff, #fbfaf6)' }}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'space-around', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                {goals.map((g, i) => {
                    const pct   = progressPct(g.savedSoFar, g.targetAmount);
                    const delay = `${(i * 0.6).toFixed(1)}s`;
                    const open  = activeId === g.id;
                    return (
                        <div key={g.id} style={{ textAlign: 'center', cursor: 'pointer', minWidth: 80 }} onClick={() => { setActiveId(open ? null : g.id); setAmount(''); }}>
                            <div style={{ animation: `bgt-floaty 6s ease-in-out ${delay} infinite` }}>
                                <GoalJar goalId={g.id} pct={pct} size={88} />
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 12, marginTop: 6, color: NAVY }}>{g.name}</div>
                            <div style={{ fontSize: 11, color: MUTED }}>{fmtGBP(g.savedSoFar)} / {fmtGBP(g.targetAmount)}</div>
                            {open && (
                                <div style={{ marginTop: 8, display: 'flex', gap: 4, justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                                    <input
                                        type="number" min="0.01" step="0.01" placeholder="£"
                                        value={amount}
                                        onChange={e => setAmount(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addContrib(g.id)}
                                        style={{ ...inputBase, width: 80, textAlign: 'center', fontSize: 14 }}
                                        autoFocus
                                    />
                                    <button onClick={() => addContrib(g.id)} disabled={saving}
                                        style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontSize: 16 }}>
                                        +
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div style={{ borderTop: `1px dashed ${BORDER}`, marginTop: 14, paddingTop: 12, textAlign: 'center', fontSize: 12, color: MUTED }}>
                Saved across all jars <b style={{ ...poppins(13), color: GREEN }}>{fmtGBP(totalSaved)}</b>
            </div>
        </div>
    );
}

// ── Child: In/Out tiles ───────────────────────────────────────────────────────
function ChildInOutTiles({ inAmount, outAmount }) {
    return (
        <div className="bgt-tiles2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div style={{ ...card, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, background: '#ecfaf2', borderColor: '#c9efdd' }}>
                <div style={{ width: 46, height: 46, borderRadius: 14, background: GREEN, display: 'grid', placeItems: 'center', fontSize: 22, color: '#fff', flexShrink: 0 }}>↑</div>
                <div>
                    <div style={{ ...lbl, color: GREEN }}>In this month</div>
                    <div style={{ ...poppins(26, 800), color: GREEN }}>{fmtGBP(inAmount)}</div>
                </div>
            </div>
            <div style={{ ...card, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, background: '#fef3ea', borderColor: '#f7dcc4' }}>
                <div style={{ width: 46, height: 46, borderRadius: 14, background: PEACH, display: 'grid', placeItems: 'center', fontSize: 22, color: NAVY, flexShrink: 0 }}>↓</div>
                <div>
                    <div style={{ ...lbl, color: ORANGE }}>Out this month</div>
                    <div style={{ ...poppins(26, 800), color: ORANGE }}>{fmtGBP(outAmount)}</div>
                </div>
            </div>
        </div>
    );
}

// ── Child: Floaty add inputs ──────────────────────────────────────────────────
function ChildFloatyInputs({ firstGoalId, onContribute, onSpend }) {
    const [inVal,  setInVal]  = useState('');
    const [outVal, setOutVal] = useState('');
    const [saving, setSaving] = useState(false);

    async function handleIn() {
        if (!inVal || Number(inVal) <= 0 || !firstGoalId) return;
        setSaving(true);
        await onContribute(firstGoalId, Number(inVal));
        setInVal('');
        setSaving(false);
    }

    async function handleOut() {
        if (!outVal || Number(outVal) <= 0) return;
        setSaving(true);
        await onSpend(Number(outVal));
        setOutVal('');
        setSaving(false);
    }

    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ ...lbl, marginBottom: 10 }}>Add money</div>
            <div className="bgt-tiles2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Money in */}
                <div style={{ background: '#fff', border: `2px solid ${GREEN}`, borderRadius: 20, padding: '8px 8px 8px 16px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: `0 8px 20px rgba(16,185,129,.15)` }}>
                    <span style={{ ...poppins(20), color: GREEN, flexShrink: 0 }}>£</span>
                    <input
                        type="number" min="0.01" step="0.01" placeholder="Money in"
                        value={inVal}
                        onChange={e => setInVal(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleIn()}
                        style={{ flex: 1, border: 'none', outline: 'none', fontFamily: "'Poppins',sans-serif", fontWeight: 600, fontSize: 16, background: 'transparent', minWidth: 0, color: NAVY }}
                    />
                    <button onClick={handleIn} disabled={saving || !firstGoalId}
                        style={{ background: GREEN, color: '#fff', border: 'none', width: 44, height: 44, borderRadius: 14, fontSize: 22, cursor: 'pointer', flexShrink: 0, opacity: firstGoalId ? 1 : 0.4 }}>+</button>
                </div>
                {/* Money out */}
                <div style={{ background: '#fff', border: `2px solid #f0b27f`, borderRadius: 20, padding: '8px 8px 8px 16px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: `0 8px 20px rgba(240,138,60,.15)` }}>
                    <span style={{ ...poppins(20), color: ORANGE, flexShrink: 0 }}>£</span>
                    <input
                        type="number" min="0.01" step="0.01" placeholder="Money out"
                        value={outVal}
                        onChange={e => setOutVal(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleOut()}
                        style={{ flex: 1, border: 'none', outline: 'none', fontFamily: "'Poppins',sans-serif", fontWeight: 600, fontSize: 16, background: 'transparent', minWidth: 0, color: NAVY }}
                    />
                    <button onClick={handleOut} disabled={saving}
                        style={{ background: PEACH, color: NAVY, border: 'none', width: 44, height: 44, borderRadius: 14, fontSize: 22, cursor: 'pointer', flexShrink: 0 }}>-</button>
                </div>
            </div>
            {!firstGoalId && (
                <div style={{ fontSize: 12, color: MUTED, marginTop: 8, textAlign: 'center' }}>
                    Add a jar above to start saving money in.
                </div>
            )}
        </div>
    );
}

// ── Child budget view ─────────────────────────────────────────────────────────
function ChildBudget({ data, spendingTotal, workspaceType, members, onAdd, onDelete, onContribute, onSpend }) {
    const goals     = data.goals || [];
    const pocket    = goals.reduce((s, g) => s + (g.weeklyIncome || 0), 0) * 4.33;
    const inAmount  = pocket > 0 ? pocket : 0;
    const outAmount = spendingTotal || 0;
    const firstGoalId = goals[0]?.id || null;

    return (
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '1rem 1rem 4rem' }}>
            <style>{RESPONSIVE_CSS}</style>

            <div style={{ marginBottom: 20 }}>
                <h2 style={{ ...poppins(24), color: NAVY, margin: '0 0 3px' }}>My money</h2>
                <div style={{ fontSize: 13, color: MUTED }}>Fill the jar. Watch it grow. 🌱</div>
            </div>

            {/* Jars */}
            {goals.length === 0 && (
                <div style={{ ...card, padding: '28px 24px', marginBottom: 20, textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 10 }}>🫙</div>
                    <h3 style={{ ...poppins(16), color: NAVY, margin: '0 0 6px' }}>No jars yet</h3>
                    <p style={{ fontSize: 13, color: MUTED, margin: '0 0 16px' }}>
                        Start a savings jar and watch it fill up as you save.
                    </p>
                    <GoalsSection goals={[]} members={[]} onAdd={onAdd} onDelete={onDelete} onContribute={onContribute} childMode />
                </div>
            )}
            {goals.length === 1 && <JarHero goal={goals[0]} onContribute={onContribute} />}
            {goals.length > 1  && <JarShelf goals={goals} onContribute={onContribute} />}

            {/* In/Out tiles */}
            {goals.length > 0 && <ChildInOutTiles inAmount={inAmount} outAmount={outAmount} />}

            {/* Floaty add inputs */}
            {goals.length > 0 && (
                <ChildFloatyInputs firstGoalId={firstGoalId} onContribute={onContribute} onSpend={onSpend} />
            )}

            {/* Add another jar (when goals exist) */}
            {goals.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                    <GoalsSection goals={[]} members={[]} onAdd={onAdd} onDelete={() => {}} onContribute={() => {}} childMode />
                </div>
            )}

            {/* Spending insights */}
            <SpendingSection workspaceType={workspaceType} members={members} />
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Budget() {
    useAutoTour('budget');
    useChatIntro('budget');
    const { currentUser, activeWorkspaceId, workspaces } = useCurrentUser();

    const ws           = (workspaces || []).find(w => w.id === activeWorkspaceId);
    const isPersonalWs = ws?.type === 'personal';
    const isChild      = currentUser?.isChildAccount === true;

    const [data,          setData]     = useState({ items: [], goals: [], debts: [], headroom: null, safespend: null });
    const [members,       setMembers]  = useState([]);
    const [spendingTotal, setSpending] = useState(0);
    const [loading,       setLoading]  = useState(true);
    const [error,         setError]    = useState('');

    const load = useCallback(async (silent = false) => {
        if (!activeWorkspaceId || !isPersonalWs) return;
        if (!silent) setLoading(true);
        setError('');
        try {
            const [bRes, mRes, sRes] = await Promise.all([
                api('/api/budget'),
                api('/api/household'),
                api('/api/spending/report'),
            ]);
            if (!bRes.ok) throw new Error('budget');
            setData(await bRes.json());
            if (mRes.ok) setMembers((await mRes.json()).members || []);
            if (sRes.ok) { const sd = await sRes.json(); setSpending(sd.total || 0); }
        } catch (err) { setError(messageFromError(err, 'Could not load your budget.')); }
        finally   { setLoading(false); }
    }, [activeWorkspaceId, isPersonalWs]);

    useEffect(() => { load(); }, [load]);

    // ── Mutation helpers (logic unchanged from Block 66/68/69) ────────────────
    async function onAdd(section, body) {
        try {
            const res = await api(`/api/budget/${section}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(body),
            });
            await load(true);
        } catch (err) { setError(messageFromError(err, 'Could not save that.')); }
    }

    async function onDelete(section, id) {
        try {
            await api(`/api/budget/${section}/${id}`, { method: 'DELETE' });
            await load(true);
        } catch (err) { setError(messageFromError(err, 'Could not delete that.')); }
    }

    async function onPatch(section, id, body) {
        try {
            const res = await api(`/api/budget/${section}/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(body),
            });
            await load(true);
        } catch (err) { setError(messageFromError(err, 'Could not update that.')); }
    }

    async function onContribute(goalId, amount) {
        try {
            const res = await api(`/api/budget/goals/${goalId}/contributions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ amount }),
            });
            await load(true);
        } catch (err) { setError(messageFromError(err, 'Could not log that contribution.')); }
    }

    // Child "money out" logs a spending entry
    async function onSpend(amount) {
        try {
            const today = new Date().toISOString().slice(0, 10);
            await api('/api/spending', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ amount, date: today, category: 'Other', note: '' }),
            });
            await load(true);
        } catch (err) { setError(messageFromError(err, 'Could not log that spend.')); }
    }

    if (!activeWorkspaceId || loading) {
        return (
            <div style={{ maxWidth: 700, margin: '0 auto', padding: '1rem' }}>
                {error   && <p style={{ color: '#b91c1c', fontSize: 13 }}>{error}</p>}
                {!activeWorkspaceId && <p style={{ color: '#bbb', fontSize: 13 }}>Connecting to workspace...</p>}
                {loading && <p style={{ color: MUTED, fontSize: 13 }}>Loading...</p>}
            </div>
        );
    }

    // ── Child variant ─────────────────────────────────────────────────────────
    if (isChild) {
        return (
            <ChildBudget
                data={data}
                spendingTotal={spendingTotal}
                workspaceType={ws?.type || 'personal'}
                members={members}
                onAdd={onAdd}
                onDelete={onDelete}
                onContribute={onContribute}
                onSpend={onSpend}
            />
        );
    }

    // ── Adult / family-owner variant ──────────────────────────────────────────
    return (
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '1rem 1rem 4rem', background: BG, minHeight: '100vh' }}>
            <style>{RESPONSIVE_CSS}</style>

            {error && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</p>}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <h2 style={{ ...poppins(24), color: NAVY, margin: '0 0 3px' }}>Budget</h2>
                        <HelpButton tourId="budget" />
                    </div>
                    <div style={{ fontSize: 13, color: MUTED }}>
                        {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} at a glance
                    </div>
                </div>
                <button data-tour-id="budget-add" style={btnPeach} onClick={() => document.getElementById('bgt-incomings')?.scrollIntoView({ behavior: 'smooth' })}>
                    + Add income or bill
                </button>
            </div>

            {/* Hero */}
            <HeroCard headroom={data.headroom} spendingTotal={spendingTotal} safespend={data.safespend} />

            {/* Quick tiles */}
            <QuickTiles headroom={data.headroom} spendingTotal={spendingTotal} items={data.items} />

            {/* Goals — pinned near top per design */}
            <GoalsSection
                goals={data.goals}
                members={members}
                onAdd={onAdd}
                onDelete={onDelete}
                onContribute={onContribute}
            />

            {/* Cash flow weekly table */}
            <CashFlowTable items={data.items} />

            {/* Debts — "chipping away, no pressure" */}
            <DebtsSection
                debts={data.debts}
                onAdd={onAdd}
                onDelete={onDelete}
                onPatch={(id, body) => onPatch('debts', id, body)}
            />

            {/* Incomings */}
            <IncomingsSection
                items={data.items}
                members={members}
                headroom={data.headroom}
                onAdd={onAdd}
                onDelete={onDelete}
                onEdit={(id, body) => onPatch('items', id, body)}
            />

            {/* Outgoings */}
            <OutgoingsSection
                items={data.items}
                members={members}
                headroom={data.headroom}
                onAdd={onAdd}
                onDelete={onDelete}
                onEdit={(id, body) => onPatch('items', id, body)}
            />

            {/* Spending insights (separate SpendingSection — Block 68) */}
            <SpendingSection
                workspaceType={ws?.type || 'personal'}
                members={members}
            />
        </div>
    );
}
